/* The Traces page's server data: an agent's runs as the page's `Run`s, and a run's
   span tree from its recorded trace. */
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { useThreads } from "@/shared/api/threads";
import { getRunTrace, traceKeys, useAgentRuns } from "@/shared/api/traces";
import { recordedSpans, toTraceRun } from "./recorded";

import type { Agent, ApiTrace, Run } from "@/shared/types";
import type { Span } from "./types";

/** An agent's runs, newest first, each titled with its conversation's title.
    `null` until they are read, and for no agent. */
export function useRecordedRuns(agentId: string | null): { runs: Run[] | null; loading: boolean } {
  const runs = useAgentRuns(agentId);
  const threads = useThreads(agentId);
  const made = useMemo(() => {
    if (!agentId || !runs.data) return null;
    const titles = new Map((threads.data ?? []).map((t) => [t.id, t.title]));
    return runs.data.map((run) => toTraceRun(run, (run.thread_id && titles.get(run.thread_id)) || null));
  }, [agentId, runs.data, threads.data]);
  return { runs: made, loading: !!agentId && runs.isPending };
}

const EMPTY: ApiTrace = { spans: [], cursor: "" };

/** Each run's span tree, from its recorded trace. Only the runs in `wanted` are
    read — every trace of a long list would be one request per row — and until one
    arrives its tree is the root alone. */
export function useSpanTrees(runs: Run[], agent: Agent, wanted: string[]): (run: Run) => Span {
  const ids = runs.filter((run) => wanted.includes(run.id)).map((run) => run.id).join(" ");
  // a stable `combine` keeps the map the same object until a trace's data changes
  const combine = useCallback(
    (results: UseQueryResult<ApiTrace>[]) => new Map(ids.split(" ").map((id, i) => [id, results[i]?.data])),
    [ids],
  );
  const byRun = useQueries({
    queries: (ids ? ids.split(" ") : []).map((id) => ({ queryKey: traceKeys.one(id), queryFn: () => getRunTrace(id) })),
    combine,
  });
  return useCallback(
    (run: Run) => recordedSpans(byRun.get(run.id) ?? EMPTY, run, agent),
    [byRun, agent],
  );
}
